/* eslint-disable react-hooks/rules-of-hooks */
import { h, Fragment } from 'preact'
import { lazy, memo, Suspense, SuspenseList } from 'preact/compat'
import { useState, useEffect, useLayoutEffect, useMemo } from 'preact/hooks'
import { useSelector, useDispatch } from 'react-redux'

import {modelTypes} from '@constants/modelTypes'
import {widgetZones} from '@constants/widgetZones'


import withProvider from '@state/withProvider'
import { fetchPage } from '@state/page/pageSlice'

import translate from '@core/translate'
import useSSR from '@core/hooks/useSSR'
import { facetEntities, productsSelector } from '@core/state/page/pageSelectors'

import { WidgetZoneProvider } from '@ui/WidgetZoneContext/WidgetZoneContext'

import { findNestedObject } from '@utils/misc'
import { useSize, setCssVar } from '@utils/dom'
import { elementReady } from '@utils/elements'

import FadeIn, { fades } from '@components/fadeIn/FadeIn'
import Loader from '@components/loader/Loader'
import NoResults from '@components/noResults/NoResults'
import ProductList from '@components/productList'

import WithSearch from '@features/elastic/core/WithSearchRenderProps'
import SearchProvider from '@features/elastic/core/SearchProvider'

import Layout from '@features/elastic/core/Layout'
import FacetSelections from '@features/elastic/facets/FacetSelections'
import PagingContainer from '@features/elastic/paging/PagingContainer'
import FacetList from '@features/elastic/facets/FacetList'
import NumberOfHits from '@features/elastic/components/NumberOfHits'

import getProductRibbons from '@features/productRibbons'
import getSelectedFacets from './getSelectedFacets'
import getSearchConfig from './getSearchConfig'
import getPageBreadcrumbs from './getPageBreadcrumbs'

import ArchiveFacetsModal from './ArchiveFacetsModal'
import ArchiveHeader from './ArchiveHeader'
import ArchiveDescription from './ArchiveDescription'

const Footer = lazy(() =>
	import(/* webpackChunkName: "footer.chunk" */ '@features/footer/components/Footer'),
)

function adjustFacetBarAccentBackground() {
    elementReady('.facet-bar').then((element) => {
        const doc = document.documentElement;

        try {
            const wsuTop = document.querySelector('.wsu-top');
            const wsuTopRects = wsuTop.getBoundingClientRect();
            const wsuFiltersRects = element.getBoundingClientRect();
            const accentBgMinHeight = `${wsuTopRects.height + (wsuFiltersRects.height / 2)}px`;
            doc.style.setProperty('--archive-bg-accent-min-height', accentBgMinHeight);
        } catch (err) {
            doc.style.setProperty('--archive-bg-accent-min-height', '175px')
        }
    });
}

export default withProvider(({ pageType }) => {
    const {
        id,
        name,
        description,
        subCategories,
        categoryBreadcrumb,
        categoryNavigation,
        displayCategoryBreadcrumb,
        pagingFilteringContext,
        pictureModel,
        q,
    } = useSelector(state => state.currentPage)

    const categories = categoryNavigation?.categories ?? []

    const dispatch = useDispatch()
    const [currentSubCategories, setCurrentSubCategories] = useState(subCategories || [])

    const facets = useSelector(facetEntities)
    const { products } = useSelector(productsSelector)
    const { totalItems } = pagingFilteringContext ?? {}
    const { isDesktop } = useSize()

    const { contentBlocks } = useSSR([
        modelTypes.categoryCurrentPage,
        modelTypes.initialState
    ]);

    // search term formatted
    const wasSearched = q ? translate('search.wassearched', q) : null;
    
    // correct breadcrumbs for page type
    const BreadcrumbsComponent = getPageBreadcrumbs(pageType)

    // config object for SearchProvider
    const config = getSearchConfig(async requestUrl => {
        await dispatch(fetchPage(requestUrl))
    })

    // eslint-disable-next-line arrow-body-style
    const selectedFacets = useMemo(() => {
        return getSelectedFacets(facets)
    }, [facets])

    // Set sub categories before render,
    // helps in reducing CLS.
    useLayoutEffect(() => {
        if (categories.length > 0) {
            const active = findNestedObject(categories ?? [], 'id', id)

            setCurrentSubCategories(active.subCategories)
        }
    }, [categories, id])

    useEffect(() => {
        // ProductList components also runs this function whenever their products change, so
        // state changes in this page component is unnecessary.
        getProductRibbons();

        // Run on resize
        window.addEventListener('resize', adjustFacetBarAccentBackground);

        // Run once
        adjustFacetBarAccentBackground();

        // Cleanup
        return () => {
            window.removeEventListener('resize', adjustFacetBarAccentBackground);
        }
    }, []);

    const showFacets = facets.length > 0;
    const showProducts = products && products.length > 0;
    const isCategoryListing = pageType === "category";
    const isSearchPage = pageType === "search";

    return (
        <WidgetZoneProvider contentBlocks={contentBlocks}>
            <SearchProvider config={config}>
                <SuspenseList revealOrder="forwards">
                    <Layout
                        header={
                            <Fragment>
                                {BreadcrumbsComponent && BreadcrumbsComponent}

                                <ArchiveHeader
                                    name={isSearchPage ? translate('search') : name}
                                    showSearchQuery={isSearchPage}
                                    subCategories={currentSubCategories}
                                />
                            </Fragment>
                        }
                        bodyContent={
                            <Fragment>
                                {(showFacets) && (
                                    <>
                                        <Suspense fallback={<Loader heights={{ desktop: '56px' }} />}>
                                            <FadeIn fade={fades.fadeIn} className="filters">
                                                <FacetList facets={facets} />
                                            </FadeIn>
                                        </Suspense>

                                        <WithSearch
                                            mapContextToProps={({ priceRange, setPriceRange, addFacet, removeFacet }) => ({
                                                priceRange,
                                                setPriceRange,
                                                addFacet,
                                                removeFacet,
                                            })}
                                        >
                                            {({ priceRange, setPriceRange, addFacet, removeFacet }) => {
                                                const showFacetSelections =
                                                    (priceRange && priceRange.length > 0) ||
                                                    (selectedFacets && selectedFacets.length > 0)

                                                return (
                                                    <>
                                                        {showFacetSelections && (
                                                            <Suspense
                                                                fallback={
                                                                    <Loader
                                                                        heights={{ desktop: '50px' }}
                                                                    />
                                                                }
                                                            >
                                                                <FacetSelections
                                                                    addFacet={addFacet}
                                                                    removeFacet={removeFacet}
                                                                    priceRange={priceRange}
                                                                    setPriceRange={setPriceRange}
                                                                    selectedOptions={selectedFacets}
                                                                />
                                                            </Suspense>
                                                        )}
                                                    </>
                                                )
                                            }}
                                        </WithSearch>
                                    </>
                                )}

                                <slot data-widget-zone={widgetZones.categoryBeforeProductList} />

                                {isCategoryListing && (
                                    <slot data-widget-zone={widgetZones.categoryListingTop} />
                                )}

                                <Suspense fallback={<Loader heights={{ desktop: '32px' }} />}>
                                    <FadeIn fade={fades.fadeIn} className="products">
                                        {showProducts && (
                                            <ProductList data={products} />
                                        )}

                                        {!showProducts && (
                                            <NoResults
                                                title={translate('category.noresults.title')}
                                                text={translate('category.noresults.text')}
                                            />
                                        )}
                                    </FadeIn>
                                </Suspense>

                                <PagingContainer />
                            </Fragment>
                        }

                        footerContent={
                            <>
                                {description && (
                                    <ArchiveDescription description={description} />
                                )}

                                <ArchiveFacetsModal
                                    titleText="Filtrera"
                                    buttonText={`Visa ${totalItems} träffar`}
                                >
                                    <Fragment>
                                        <WithSearch
                                            mapContextToProps={({
                                                priceRange,
                                                setPriceRange,
                                                addFacet,
                                                removeFacet,
                                            }) => ({
                                                priceRange,
                                                setPriceRange,
                                                addFacet,
                                                removeFacet,
                                            })}
                                        >
                                            {({ priceRange, setPriceRange, addFacet, removeFacet }) => {
                                                const showFacetSelections =
                                                    (priceRange && priceRange.length > 0) ||
                                                    (selectedFacets && selectedFacets.length > 0)

                                                return (
                                                    <>
                                                        {showFacetSelections && (
                                                            <Suspense
                                                                fallback={
                                                                    <Loader
                                                                        heights={{ desktop: '50px' }}
                                                                    />
                                                                }
                                                            >
                                                                <FacetSelections
                                                                    addFacet={addFacet}
                                                                    removeFacet={removeFacet}
                                                                    priceRange={priceRange}
                                                                    setPriceRange={setPriceRange}
                                                                    selectedOptions={selectedFacets}
                                                                />
                                                            </Suspense>
                                                        )}
                                                    </>
                                                )
                                            }}
                                        </WithSearch>

                                        <Suspense fallback={<Loader heights={{ desktop: '56px' }} />}>
                                            <FadeIn fade={fades.fadeIn} className="modal-facets__list">
                                                <FacetList facets={facets} />
                                            </FadeIn>
                                        </Suspense>
                                    </Fragment>
                                </ArchiveFacetsModal>
                            </>
                        } />
                </SuspenseList>
            </SearchProvider>
        </WidgetZoneProvider>
    )
});